/**
 * Global Search Service
 * Provides unified search across all admin dashboard modules
 */

import { api } from './api';

/**
 * Search across all modules
 * @param {string} query - Search query
 * @returns {Promise<Object>} Search results grouped by module
 */
export const globalSearch = async (query) => {
  if (!query || query.trim().length < 2) {
    return {
      users: [],
      courses: [],
      content: [],
      total: 0
    };
  }

  try {
    // Perform parallel searches across different modules
    const [usersResult, coursesResult, contentResult] = await Promise.allSettled([
      // Search users
      api.get(`/api/admin/users?search=${encodeURIComponent(query)}&limit=5`).catch(() => ({ users: [] })),
      // Search courses (when API is available)
      Promise.resolve({ courses: [] }), // Placeholder
      // Search content (when API is available)
      Promise.resolve({ content: [] }) // Placeholder
    ]);

    const users = usersResult.status === 'fulfilled' ? (usersResult.value?.users || usersResult.value || []) : [];
    const courses = coursesResult.status === 'fulfilled' ? (coursesResult.value?.courses || []) : [];
    const content = contentResult.status === 'fulfilled' ? (coursesResult.value?.content || []) : [];

    return {
      users: users.slice(0, 5), // Limit to 5 results per category
      courses: courses.slice(0, 5),
      content: content.slice(0, 5),
      total: users.length + courses.length + content.length,
      query
    };
  } catch (error) {
    console.error('Global search error:', error);
    return {
      users: [],
      courses: [],
      content: [],
      total: 0,
      error: error.message
    };
  }
};

/**
 * Format search result for display
 * @param {Object} result - Search result object
 * @param {string} type - Result type (user, course, content)
 * @returns {Object} Formatted result
 */
export const formatSearchResult = (result, type) => {
  switch (type) {
    case 'user':
      return {
        id: result.id,
        title: `${result.first_name || ''} ${result.last_name || ''}`.trim() || result.email,
        subtitle: result.email,
        type: 'user',
        icon: 'person',
        url: `/admin-dashboard#user-management`,
        metadata: {
          role: result.role,
          status: result.is_active ? 'Active' : 'Inactive'
        }
      };
    case 'course':
      return {
        id: result.id,
        title: result.title || result.name,
        subtitle: result.description?.substring(0, 100),
        type: 'course',
        icon: 'school',
        url: `/admin-dashboard#course-management`,
        metadata: {
          students: result.student_count || 0,
          status: result.status
        }
      };
    case 'content':
      return {
        id: result.id,
        title: result.title || result.name,
        subtitle: result.content?.substring(0, 100),
        type: 'content',
        icon: 'article',
        url: `/admin-dashboard#course-management`,
        metadata: {
          type: result.content_type,
          views: result.views || 0
        }
      };
    default:
      return result;
  }
};

/**
 * Quick search - returns top 3 results
 * @param {string} query - Search query
 * @returns {Promise<Array>} Top 3 formatted results
 */
export const quickSearch = async (query) => {
  const results = await globalSearch(query);
  const formatted = [
    ...results.users.map(r => formatSearchResult(r, 'user')),
    ...results.courses.map(r => formatSearchResult(r, 'course')),
    ...results.content.map(r => formatSearchResult(r, 'content'))
  ];
  
  return formatted.slice(0, 3);
};



